import type { VmSizePackage } from "@/modules/compute/VmSize.types.ts";
import {
    calculateSizeInputs,
    forceSizeForOs,
    getDefaultSize,
    getSizeParams,
    onSizeChange,
    type SizeInputs,
    type SizeViewModel,
} from "@/modules/compute/commonViewModel.ts";
import { getDefaultDesignatedPoolForLocation } from "@/modules/compute/pool/pool.ts";
import type { VmSizeParams } from "@/modules/compute/vmSizeSelectUtils.ts";
import type { LocationInputs } from "@/modules/location/LocationField.tsx";
import type { LocationsForType } from "@/modules/location/location.ts";
import type { LocationSlug } from "@/modules/location/locationQuery.ts";
import type * as hostPoolsQuery from "@/modules/pools/hostPoolsQuery.ts";
import type { HostPoolWithType } from "@/modules/pools/hostPoolsQuery.ts";
import { DefaultNodeCount } from "@/modules/services/FailoverNodeSelect.tsx";
import type { ServiceOs } from "@/modules/services/os/serviceOs.ts";
import type { GetField, SetField } from "@/utils/getField.tsx";
import type {
    KubernetesProviderWorker,
    ManagedServiceCreateBody,
    ServiceImage,
} from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";

export interface ServiceCreateInputs
    extends Pick<ManagedServiceCreateBody, "display_name" | "billing_account_id">, LocationInputs, SizeInputs {
    service: ServiceOs;
    network_uuid?: ManagedServiceCreateBody["package_parameters"]["network_uuid"];
    reserve_public_ip: boolean;
    failover_nodes: number;

    begin?: string;
    timeZone?: string;
    machineImageVersion?: boolean;
    kubernetesVersion?: boolean;
    node_pools?: KubernetesProviderWorker[];
}

export class ServiceCreateViewModel implements SizeViewModel {
    get: GetField<ServiceCreateInputs> = (_n) => undefined;
    set: SetField<ServiceCreateInputs> = (_n, _v) => {};
    _sizeParams: VmSizeParams;

    constructor(
        private readonly availableImages: ServiceImage[],
        private readonly locations: LocationsForType,
        public readonly _packages: VmSizePackage[],
        private readonly pools: hostPoolsQuery.HostPoolWithType[],
        private readonly _base: Partial<ServiceCreateInputs> | undefined,
    ) {
        this._sizeParams = getSizeParams();
    }

    attachGetterAndSetter(getter: typeof this.get, setter: typeof this.set) {
        this.get = getter;
        this.set = setter;
    }

    get _os() {
        return this.get("service") ?? this._base?.service ?? this.getDefaultService();
    }

    get _diskSize() {
        // Services are never cloned from a previous disk?
        return undefined;
    }

    onChangeService = (newValue: ServiceOs) => {
        console.log("onChangeService, value:", newValue);
        this.set("service", newValue);

        forceSizeForOs.call(this);

        this.set("failover_nodes", this.getDefaultNodeCountForService(newValue));
    };

    onLocationChange = (newValue: string) => {
        console.log("onLocationChange, value:", newValue);
        this.set("location", newValue);
        this.set("network_uuid", undefined); // field selects it's own default and sets it to form field
        this.onPoolChange(notNull(getDefaultDesignatedPoolForLocation(this.pools, newValue)));
    };

    getSizeParams(): VmSizeParams {
        return this._sizeParams;
    }

    onPoolChange = (pool: HostPoolWithType) => {
        console.debug("onPoolChange, value: %o", pool);
        this._sizeParams = getSizeParams(pool.guest_limits);

        this.onSizeChange(getDefaultSize.bind(this)());
    };

    onSizeChange = onSizeChange.bind(this);

    private getDefaultLocation(): LocationSlug {
        return this.locations.defaultLocation;
    }

    private getDefaultService(): ServiceOs {
        return {
            os_name: this.availableImages[0].service_name,
            os_version: this.availableImages[0].versions[0],
        };
    }

    private getDefaultNodeCountForService(service: ServiceOs): number {
        const item = this.availableImages.find((i) => i.service_name === service.os_name);
        return item?.is_multi_node_available ? DefaultNodeCount : 0;
        /* if (service.os_name === "mariadb") return 0;
        return DefaultNodeCount; */
    }

    private getDefaultDesignatedPool() {
        const pool = getDefaultDesignatedPoolForLocation(this.pools, this.getDefaultLocation());
        this.onPoolChange(notNull(pool));
    }

    getDefaultValues(): Omit<ServiceCreateInputs, "billing_account_id"> {
        console.log("Getting form default values");

        /*
        begin: properties.spec.maintenance.timeWindow.begin.slice(0, 6),
        timeZone: properties.spec.maintenance.timeWindow.begin.slice(
            6,
            properties.spec.maintenance.timeWindow.begin.length,
        ),
        machineImageVersion: properties.spec.maintenance.autoUpdate.machineImageVersion,
        kubernetesVersion: properties.spec.maintenance.autoUpdate.kubernetesVersion,
         */
        const base = this._base;
        this.getDefaultDesignatedPool();
        return {
            display_name: "",
            reserve_public_ip: true,
            network_uuid: undefined, // react-hook-form does not support `undefined` for controlled fields
            node_pools: undefined,
            billing_account_id: null,
            ...base,

            location: base?.location ?? this.getDefaultLocation(),
            failover_nodes: base?.failover_nodes ?? this.getDefaultNodeCountForService(this.getDefaultService()),
            service: this._os,
            ...calculateSizeInputs.call(this, base?.size),

            begin: "030000",
            timeZone: "+0000",
            machineImageVersion: true,
            kubernetesVersion: true,
        };
    }
}
